import { useCallback, useMemo, ReactNode, useEffect } from "react";
import {
  AuthProvider as PropelAuthProvider,
  RedirectToLogin,
  useAuthInfo,
  useLogoutFunction,
  useRedirectFunctions,
} from "@propelauth/react";
import { CircularProgress } from "@mui/material";
import * as Sentry from "@sentry/react";
import { isoDuration } from "@musement/iso-duration";

import { Identity } from "../types";
import { IdentityContext, AuthenticationRedirectContext } from "../context";

function useRefreshAccessTokenBeforeExpiration({
  refreshLeewaySeconds = 15,
} = {}) {
  // Propel will refresh the token in the background, but only on a fixed interval or when
  // tab focus changes, and does not take into account when the token actually expires.
  // This is a best-effort attempt to keep tokens evergreen without having to entangle the api layer
  // with the auth layer. This won't be perfect, but I'm hoping that it will solve most expired token
  // issues.
  //
  // If we find that we're still getting too many expired token problems we should allow the api client
  // to force a token refresh and then potentially retry the request. However this both break separation
  // of concerns, and also brings up issues around the combination of side effects and retries that I'd
  // like to avoid until we feel like we absolutely need to.

  const { loading, accessTokenExpiresAtSeconds, refreshAuthInfo } =
    useAuthInfo();

  useEffect(() => {
    if (loading || accessTokenExpiresAtSeconds == null) return;

    const nowSeconds = Math.ceil(Date.now() / 1_000);

    const doRefresh = async () => {
      if (import.meta.env.MODE === "development") {
        console.debug("Refreshing PropelAuth access token");
      }

      try {
        await refreshAuthInfo();
        if (import.meta.env.MODE === "development") {
          console.debug("PropelAuth access token refreshed");
        }
      } catch (e) {
        if (import.meta.env.MODE === "development") {
          console.error("Error refreshing PropelAuth access token", e);
        } else {
          Sentry.captureException(e);
        }
      }
    };

    if (import.meta.env.MODE === "development") {
      const expirationDate = new Date(accessTokenExpiresAtSeconds * 1_000);
      const expirationDuration = isoDuration({
        seconds: accessTokenExpiresAtSeconds - nowSeconds,
      })
        .normalize()
        .humanize("en");

      console.debug(
        `Current PropelAuth access token will expire at ${expirationDate} -- ${expirationDuration} from now`
      );
    }

    if (accessTokenExpiresAtSeconds - nowSeconds <= refreshLeewaySeconds) {
      // when the token updates, the effect will run again, and the new update will be scheduled.
      doRefresh();
      return;
    }

    const refreshDeadlineSeconds =
      accessTokenExpiresAtSeconds - refreshLeewaySeconds;
    const refreshDeadlineMs = (refreshDeadlineSeconds - nowSeconds) * 1_000;

    if (import.meta.env.MODE === "development") {
      const expirationDate = new Date(accessTokenExpiresAtSeconds * 1_000);
      const expirationDuration = isoDuration({
        seconds: refreshDeadlineMs / 1_000,
      })
        .normalize()
        .humanize("en");
      console.debug(
        `Scheduling PropelAuth access token refresh for ${expirationDate} -- ${expirationDuration} from now`
      );
    }

    const delayedRefresh = setTimeout(() => {
      doRefresh();
    }, refreshDeadlineMs);

    return () => {
      clearTimeout(delayedRefresh);
    };
  }, [
    accessTokenExpiresAtSeconds,
    refreshAuthInfo,
    refreshLeewaySeconds,
    loading,
  ]);
}

// This is awkward, but it has to be in a separate component for all the
// context consumers to work correctly.
function PropelAuthConsumer({ children }: { children: ReactNode }) {
  const authInfo = useAuthInfo();
  const loading = authInfo.loading;
  const isAuthenticated = authInfo.isLoggedIn;
  const accessToken = authInfo.accessToken;
  const email = authInfo.user?.email;
  const accountName = authInfo.orgHelper?.getOrgs()[0]?.orgName;
  const isImpersonating = authInfo.isImpersonating;
  const impersonatorUserId = authInfo.impersonatorUserId;

  const doLogout = useLogoutFunction();
  const { redirectToLoginPage, redirectToAccountPage } = useRedirectFunctions();

  useRefreshAccessTokenBeforeExpiration();

  const getAuthorizationHeaders = useMemo(() => {
    if (!accessToken) return () => ({});
    return () => ({ Authorization: `Bearer ${accessToken}` });
  }, [accessToken]);

  const logout = useCallback(async () => {
    doLogout(true);
  }, [doLogout]);

  const login = useCallback(async () => {
    redirectToLoginPage();
  }, [redirectToLoginPage]);

  const settings = useCallback(async () => {
    redirectToAccountPage();
  }, [redirectToAccountPage]);

  const api: Identity = useMemo(
    () => ({
      loading,
      isAuthenticated: !!isAuthenticated,
      getAuthorizationHeaders,
      logout,
      login,
      settings,
      user: { email, accountName },
      isImpersonating,
      impersonatorUserId,
    }),
    [
      loading,
      isAuthenticated,
      logout,
      login,
      settings,
      getAuthorizationHeaders,
      email,
      accountName,
      isImpersonating,
      impersonatorUserId,
    ]
  );

  return (
    <IdentityContext.Provider value={api}>{children}</IdentityContext.Provider>
  );
}

function WrappedRedirectToLogin() {
  return <RedirectToLogin postLoginRedirectUrl={window.location.href} />;
}

export default function PropelAuthIdentity({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <PropelAuthProvider
      authUrl={import.meta.env.VITE_AUTH_URL || ""}
      defaultDisplayWhileLoading={<CircularProgress />}
    >
      <AuthenticationRedirectContext.Provider value={WrappedRedirectToLogin}>
        <PropelAuthConsumer>{children}</PropelAuthConsumer>
      </AuthenticationRedirectContext.Provider>
    </PropelAuthProvider>
  );
}
